import { v4 as uuidv4 } from "uuid";
var W3CWebSocket = require("websocket").w3cwebsocket;

// ComfyUIApi 类
export class ComfyUIApi {
  private apiUrl: string;
  private wsUrl: string;

  constructor() {
    this.apiUrl = `https://${process.env.COMYUI_API_URL}`;
    this.wsUrl = `wss://${process.env.COMYUI_API_URL}`;
  }

  // 获取历史记录
  async getHistory(promptId: string): Promise<any> {
    const response = await fetch(`${this.apiUrl}/history/${promptId}`);
    return await response.json();
  }

  // 获取图片数据
  async getImage(
    filename: string,
    subfolder: string,
    type: string
  ): Promise<string | null> {
    const response = await fetch(
      `${this.apiUrl}/view?filename=${filename}&subfolder=${subfolder}&type=${type}`
    );
    if (response.ok) {
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    }
    console.error("无法下载图片:", filename);
    return null;
  }

  // 处理历史记录和图片
  async processHistory(promptId: string): Promise<string | null> {
    const history = await this.getHistory(promptId);
    if (!history) {
      console.log("未能获取历史记录");
      return null;
    }

    const historyData = history[promptId];
    if (!historyData) {
      console.log(`未能找到prompt_id为${promptId}的历史数据`);
      return null;
    }

    // 只取指定nodeId
    const nodeId = "83";
    const nodeOutput = historyData.outputs[nodeId];
    if (nodeOutput.images) {
      const image = nodeOutput.images[0];
      console.log(`可下载图片文件名:${image.filename}`);
      const imageData = await this.getImage(
        image.filename,
        image.subfolder,
        image.type
      );
      if (imageData) {
        return imageData;
      } else {
        console.log(`未能下载图片: ${image.filename}`);
      }
    }
    return null;
  }

  // 上传原始图片
  async uploadOriginalImage(
    imageUrl: string
  ): Promise<{ name: string; subfolder: string }> {
    const date = new Date();
    const response = await fetch(`${this.apiUrl}/upload/image_url`, {
      method: "POST",
      body: JSON.stringify({
        image_url: imageUrl,
        type: "input",
        subfolder: "",
      }),
    });
    const responseData = await response.json();
    return { name: responseData.name, subfolder: "" };
  }

  // 上传蒙版图片
  async uploadMaskImage(maskBase64: string, originalRef: any): Promise<string> {
    const uploadMaskResponse = await fetch(
      `${this.apiUrl}/upload/mask_base64`,
      {
        method: "POST",
        body: JSON.stringify({
          mask_base64: maskBase64,
          original_ref: originalRef,
          subfolder: originalRef.subfolder,
          type: "input",
        }),
      }
    );
    const uploadMaskResponseData = await uploadMaskResponse.json();
    return uploadMaskResponseData.name;
  }

  // 处理图片并返回结果
  async processImage(
    originalImageBase64: string,
    maskImageBase64: string,
    workflow: any,
    onProgress: (executionData: any) => void
  ): Promise<string | null> {
    return new Promise(async (resolve, reject) => {
      try {
        // 上传原始图片
        const { name: imageName, subfolder } = await this.uploadImage(originalImageBase64);

        // 上传蒙版图片
        const originalRef = {
          filename: imageName,
          type: "input",
          subfolder: subfolder,
        };
        const { name: maskImageName } = await this.uploadImage(
          maskImageBase64,
          "input",
          subfolder,
          originalRef
        );

        // 连接WebSocket
        const clientId = uuidv4();
        const clientSocket = new W3CWebSocket(
          `${this.wsUrl}/ws?clientId=${clientId}`
        );

        clientSocket.onerror = function () {
          reject("WebSocket连接错误");
        };

        let prompt_id = "";

        clientSocket.onopen = async () => {
          // 修改workflow参数
          workflow[33].inputs.image = `${maskImageName} [input]`;
          workflow[33].inputs.save_to_workflow = false;

          // 发送prompt数据到服务端
          const prompts = { prompt: workflow, client_id: clientId };
          const response = await fetch(`${this.apiUrl}/prompt`, {
            method: "POST",
            body: JSON.stringify(prompts),
          });
          const prompt_response = await response.json();
          console.log("prompt_response", prompt_response);
          prompt_id = prompt_response["prompt_id"];
        };

        clientSocket.onmessage = async (e: any) => {
          const message = JSON.parse(e.data);

          if (message.type === "executing") {
            const executionData = message.data;
            onProgress(executionData);
            if (
              executionData.node === null &&
              executionData.prompt_id === prompt_id
            ) {
              console.log("执行完成");
              const outputImages = await this.processHistory(prompt_id);
              clientSocket.close();
              resolve(outputImages);
            }
          } else if (message.type === "binary") {
            console.log("收到二进制数据，已忽略");
          }
        };

        clientSocket.onclose = () => {
          console.log("Disconnected from server");
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  // 上传图片
  async uploadImage(
    imageData: string,
    type: string = "input",
    subfolder: string = "",
    originalRef?: { filename: string; type: string; subfolder: string }
  ): Promise<{ name: string; subfolder: string }> {
    const body: any = {
      image_base64: imageData,
      type: type,
      subfolder: subfolder,
    };

    if (originalRef) {
      body.original_ref = originalRef;
    }

    const response = await fetch(`${this.apiUrl}/upload/image_base64`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    const responseData = await response.json();
    return { name: responseData.name, subfolder: responseData.subfolder };
  }

  // 使用示例
  // const comfyApi = new ComfyUIApi();
  // const images = await comfyApi.processHistory('your_prompt_id');

}
